import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/auth/AuthContext";
import { Plus, Trash2, X, Copy, Mail, UserCheck, UserX, AlertCircle, Users, Link as LinkIcon, CheckCircle2, KeyRound } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAGES = [
  { key: "dashboard", label: "Dashboard" },
  { key: "tasks", label: "Tasks" },
  { key: "orders", label: "Comenzi" },
  { key: "products", label: "Produse" },
  { key: "returns", label: "Retururi & Garanții" },
  { key: "emag_rmas", label: "Retururi eMAG" },
  { key: "messages", label: "Mesaje eMAG" },
  { key: "email_templates", label: "Șabloane Email" },
  { key: "mail", label: "Mail (Inbox personal)" },
  { key: "reports", label: "Rapoarte" },
  { key: "automations", label: "Automatizări" },
  { key: "imports", label: "Importuri" },
  { key: "invoices", label: "Facturi" },
  { key: "customers", label: "Clienți" },
  { key: "notes", label: "Notițe" },
  { key: "integrations", label: "Integrări" },
];

export default function Team() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [boards, setBoards] = useState([]);
  const [selBoard, setSelBoard] = useState("");
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [newInvite, setNewInvite] = useState({ email: "", name: "", allowed_pages: ["tasks"], board_access: [] });
  const [createdInvite, setCreatedInvite] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  // Edit-access modal state
  const [editAccessUser, setEditAccessUser] = useState(null);
  const [accessForm, setAccessForm] = useState({ allowed_pages: [], board_access: [] });
  const [savingAccess, setSavingAccess] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [u, i, b] = await Promise.all([
        axios.get(`${API}/auth/users`),
        axios.get(`${API}/auth/invites`),
        axios.get(`${API}/boards`),
      ]);
      setUsers(u.data.users || []);
      setInvites(i.data.invites || []);
      setBoards(b.data.boards || []);
      if (b.data.boards?.length && !selBoard) setSelBoard(b.data.boards[0].id);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  };

  const loadMembers = async (boardId) => {
    if (!boardId) return;
    const r = await axios.get(`${API}/boards/${boardId}/members`);
    setMembers(r.data.members || []);
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { if (selBoard) loadMembers(selBoard); }, [selBoard]);

  const togglePage = (key) => setNewInvite((p) => ({
    ...p, allowed_pages: p.allowed_pages.includes(key) ? p.allowed_pages.filter((x) => x !== key) : [...p.allowed_pages, key]
  }));

  const setBoardRole = (boardId, role) => setNewInvite((p) => {
    const others = (p.board_access || []).filter((b) => b.board_id !== boardId);
    if (!role) return { ...p, board_access: others };
    return { ...p, board_access: [...others, { board_id: boardId, role }] };
  });
  const getBoardRole = (boardId) => (newInvite.board_access || []).find((b) => b.board_id === boardId)?.role || "";

  const createInvite = async () => {
    setError(""); setCreatedInvite(null);
    try {
      const r = await axios.post(`${API}/auth/invites`, newInvite);
      const link = `${window.location.origin}/invite/${r.data.token}`;
      setCreatedInvite({ ...r.data, link });
      setNewInvite({ email: "", name: "", allowed_pages: ["tasks"], board_access: [] });
      load();
    } catch (e) { setError(e.response?.data?.detail || e.message); }
  };

  const deleteInvite = async (token) => {
    await axios.delete(`${API}/auth/invites/${token}`);
    load();
  };

  const toggleActive = async (u) => {
    await axios.put(`${API}/auth/users/${u.id}/active`, { is_active: !u.is_active });
    load();
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Ștergi utilizatorul ${u.email}?`)) return;
    await axios.delete(`${API}/auth/users/${u.id}`);
    load();
  };

  const openEditAccess = async (u) => {
    setError("");
    try {
      const r = await axios.get(`${API}/auth/users/${u.id}/access`);
      setAccessForm({
        allowed_pages: r.data.allowed_pages || [],
        board_access: r.data.board_access || [],
      });
      setEditAccessUser(u);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
  };

  const toggleAccessPage = (key) => setAccessForm((p) => ({
    ...p,
    allowed_pages: p.allowed_pages.includes(key)
      ? p.allowed_pages.filter((x) => x !== key)
      : [...p.allowed_pages, key],
  }));

  const setAccessBoardRole = (boardId, role) => setAccessForm((p) => {
    const others = (p.board_access || []).filter((b) => b.board_id !== boardId);
    if (!role) return { ...p, board_access: others };
    return { ...p, board_access: [...others, { board_id: boardId, role }] };
  });
  const getAccessBoardRole = (boardId) => (accessForm.board_access || []).find((b) => b.board_id === boardId)?.role || "";

  const saveAccess = async () => {
    if (!editAccessUser) return;
    setSavingAccess(true); setError("");
    try {
      await axios.put(`${API}/auth/users/${editAccessUser.id}/access`, accessForm);
      setEditAccessUser(null);
      load();
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setSavingAccess(false); }
  };

  const addMember = async (userId, role) => {
    await axios.post(`${API}/boards/${selBoard}/members`, { user_id: userId, role });
    loadMembers(selBoard);
  };

  const removeMember = async (userId) => {
    await axios.delete(`${API}/boards/${selBoard}/members/${userId}`);
    loadMembers(selBoard);
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (user?.role !== "owner") {
    return <div className="p-8 text-center text-zinc-500">Doar Owner-ul are acces la această pagină.</div>;
  }

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8" data-testid="team-page">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-50">Echipa</h1>
          <p className="text-sm text-zinc-500">Gestionează utilizatorii și invitațiile</p>
        </div>
        <button data-testid="new-invite-btn" onClick={() => { setShowInvite(true); setCreatedInvite(null); }}
          className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-b from-orange-500 to-rose-500 px-4 py-2 text-sm font-medium text-white">
          <Plus className="h-4 w-4" /> Invitație nouă
        </button>
      </div>

      {error && <div className="mb-4 flex items-start gap-2 rounded-xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-200"><AlertCircle className="mt-0.5 h-4 w-4" />{error}<button onClick={() => setError("")} className="ml-auto"><X className="h-4 w-4" /></button></div>}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Users */}
        <section>
          <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-zinc-400">
            <Users className="h-4 w-4" /> Utilizatori ({users.length})
          </h2>
          <div className="space-y-2">
            {loading ? <p className="text-sm text-zinc-500">Se încarcă...</p> :
              users.map((u) => (
                <div key={u.id} data-testid={`user-${u.id}`} className="flex items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-950/60 p-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-orange-500 to-rose-500 text-sm font-bold text-white">
                    {(u.name || u.email)[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="truncate text-sm font-medium text-zinc-100">{u.name || "—"}</p>
                    <p className="truncate text-xs text-zinc-500">{u.email}</p>
                  </div>
                  <span className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${u.role === "owner" ? "border-orange-500/40 bg-orange-500/10 text-orange-200" : "border-zinc-700 bg-zinc-800 text-zinc-300"}`}>{u.role}</span>
                  {u.id !== user.id && (
                    <>
                      {u.role !== "owner" && (
                        <button data-testid={`edit-access-${u.id}`} onClick={() => openEditAccess(u)} title="Editează acces (pagini + board-uri)"
                          className="rounded-lg p-1.5 text-sky-300 hover:bg-sky-500/10">
                          <KeyRound className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <button onClick={() => toggleActive(u)} title={u.is_active ? "Dezactivează" : "Activează"}
                        className={`rounded-lg p-1.5 ${u.is_active ? "text-emerald-400 hover:bg-emerald-500/10" : "text-zinc-500 hover:bg-zinc-800"}`}>
                        {u.is_active ? <UserCheck className="h-3.5 w-3.5" /> : <UserX className="h-3.5 w-3.5" />}
                      </button>
                      <button onClick={() => deleteUser(u)} className="rounded-lg p-1.5 text-zinc-500 hover:bg-rose-500/10 hover:text-rose-400">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                </div>
              ))}
          </div>

          <h2 className="mb-3 mt-6 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-zinc-400">
            <Mail className="h-4 w-4" /> Invitații în așteptare ({invites.filter((i) => !i.used).length})
          </h2>
          <div className="space-y-2">
            {invites.filter((i) => !i.used).length === 0 && <p className="text-xs text-zinc-600">Nicio invitație activă</p>}
            {invites.filter((i) => !i.used).map((inv) => {
              const link = `${window.location.origin}/invite/${inv.token}`;
              return (
                <div key={inv.token} className="flex items-center gap-2 rounded-xl border border-zinc-800 bg-zinc-950/60 p-3">
                  <Mail className="h-4 w-4 text-zinc-500" />
                  <div className="flex-1 min-w-0">
                    <p className="truncate text-sm text-zinc-100">{inv.email}</p>
                    <p className="truncate text-[10px] text-zinc-500">Link: {link}</p>
                  </div>
                  <button onClick={() => copyLink(link)} className="rounded-lg border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-200 hover:border-orange-500/40 hover:text-orange-200">
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => deleteInvite(inv.token)} className="rounded-lg p-1.5 text-zinc-500 hover:text-rose-400">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        </section>

        {/* Board sharing */}
        <section>
          <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-zinc-400">
            <LinkIcon className="h-4 w-4" /> Share board cu angajații
          </h2>
          <select value={selBoard} onChange={(e) => setSelBoard(e.target.value)} data-testid="board-select"
            className="mb-3 w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none">
            {boards.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">Membri activi pe board</p>
            <div className="space-y-2 mb-4">
              {members.filter((m) => m.role !== "owner").length === 0 && <p className="text-xs text-zinc-600">Doar tu (owner). Adaugă membri mai jos.</p>}
              {members.filter((m) => m.role !== "owner").map((m) => (
                <div key={m.user_id} className="flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900/60 p-2">
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-zinc-800 text-xs text-zinc-300">{(m.user?.name || m.user?.email || "?")[0].toUpperCase()}</div>
                  <div className="flex-1 min-w-0">
                    <p className="truncate text-xs text-zinc-100">{m.user?.name || m.user?.email}</p>
                    <p className="truncate text-[10px] text-zinc-500">{m.user?.email}</p>
                  </div>
                  <select value={m.role} onChange={(e) => addMember(m.user_id, e.target.value)}
                    className="rounded border border-zinc-800 bg-zinc-900 px-2 py-1 text-[10px] text-zinc-200">
                    <option value="editor">Editor</option>
                    <option value="viewer">Viewer</option>
                  </select>
                  <button onClick={() => removeMember(m.user_id)} className="rounded p-1 text-zinc-500 hover:text-rose-400"><X className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">Adaugă utilizator existent</p>
            {users.filter((u) => u.role !== "owner" && !members.find((m) => m.user_id === u.id)).length === 0 ? (
              <p className="text-xs text-zinc-600">Toți utilizatorii au deja acces. Creează o invitație nouă pentru a adăuga angajați.</p>
            ) : (
              <div className="space-y-1.5">
                {users.filter((u) => u.role !== "owner" && !members.find((m) => m.user_id === u.id)).map((u) => (
                  <div key={u.id} className="flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900/40 p-2">
                    <span className="flex-1 truncate text-xs text-zinc-200">{u.name || u.email}</span>
                    <button onClick={() => addMember(u.id, "editor")} className="rounded border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-[10px] text-emerald-200 hover:bg-emerald-500/20">+ Editor</button>
                    <button onClick={() => addMember(u.id, "viewer")} className="rounded border border-sky-500/40 bg-sky-500/10 px-2 py-1 text-[10px] text-sky-200 hover:bg-sky-500/20">+ Viewer</button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <p className="mt-3 text-[10px] text-zinc-600">
            <strong>Editor:</strong> creează/editează task-uri și categorii ·
            <strong> Viewer:</strong> doar vede și bifează done/pași
          </p>
        </section>
      </div>

      {/* Invite modal */}
      {showInvite && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => { setShowInvite(false); setCreatedInvite(null); }}>
          <div className="w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-950 p-6" onClick={(e) => e.stopPropagation()}>
            {!createdInvite ? (
              <>
                <h3 className="mb-1 text-lg font-semibold text-zinc-100">Invitație nouă</h3>
                <p className="mb-4 text-xs text-zinc-500">Generează un link pe care îl trimiți angajatului. Acesta își setează parola.</p>
                <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">Email *</label>
                <input data-testid="invite-email-input" type="email" value={newInvite.email} onChange={(e) => setNewInvite({ ...newInvite, email: e.target.value })}
                  className="mb-3 w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none" placeholder="angajat@email.com" />
                <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">Nume (opțional)</label>
                <input value={newInvite.name} onChange={(e) => setNewInvite({ ...newInvite, name: e.target.value })}
                  className="mb-3 w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none" placeholder="Nume Prenume" />
                <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">Pagini accesibile</label>
                <div className="mb-4 flex flex-wrap gap-1.5">
                  {PAGES.map((p) => (
                    <button key={p.key} type="button" onClick={() => togglePage(p.key)}
                      className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition ${newInvite.allowed_pages.includes(p.key) ? "border-orange-500/40 bg-orange-500/15 text-orange-200" : "border-zinc-800 bg-zinc-900 text-zinc-400"}`}>
                      {p.label}
                    </button>
                  ))}
                </div>

                {newInvite.allowed_pages.includes("tasks") && boards.length > 0 && (
                  <div className="mb-4 rounded-xl border border-zinc-800 bg-zinc-900/40 p-3">
                    <label className="mb-2 block text-[10px] uppercase tracking-wider text-zinc-500">Acces pe board-uri (pentru Tasks)</label>
                    <div className="space-y-1.5">
                      {boards.map((b) => {
                        const role = getBoardRole(b.id);
                        return (
                          <div key={b.id} data-testid={`invite-board-${b.id}`} className="flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900/60 p-2">
                            <span className="flex-1 truncate text-xs text-zinc-200">{b.name}</span>
                            <div className="flex gap-1">
                              {[
                                { v: "", l: "Fără" },
                                { v: "viewer", l: "Viewer" },
                                { v: "editor", l: "Editor" },
                              ].map((opt) => (
                                <button key={opt.v || "none"} type="button" onClick={() => setBoardRole(b.id, opt.v)}
                                  className={`rounded-md border px-2 py-1 text-[10px] font-medium transition ${
                                    role === opt.v
                                      ? opt.v === "editor"
                                        ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-200"
                                        : opt.v === "viewer"
                                        ? "border-sky-500/40 bg-sky-500/15 text-sky-200"
                                        : "border-zinc-700 bg-zinc-800 text-zinc-200"
                                      : "border-zinc-800 bg-zinc-900 text-zinc-500 hover:text-zinc-300"
                                  }`}>
                                  {opt.l}
                                </button>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <p className="mt-2 text-[10px] text-zinc-600">
                      💡 La acceptarea invitației, angajatul va primi automat accesul selectat pe board-urile alese.
                    </p>
                  </div>
                )}
                <div className="flex gap-2">
                  <button onClick={() => setShowInvite(false)} className="flex-1 rounded-lg border border-zinc-800 bg-zinc-900 py-2 text-sm text-zinc-300">Anulează</button>
                  <button data-testid="invite-create-btn" disabled={!newInvite.email} onClick={createInvite}
                    className="flex-1 rounded-lg bg-gradient-to-b from-orange-500 to-rose-500 py-2 text-sm font-medium text-white disabled:opacity-50">Generează link</button>
                </div>
              </>
            ) : (
              <>
                <div className="mb-4 flex items-center gap-3 rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3">
                  <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                  <div>
                    <p className="text-sm font-semibold text-emerald-100">Invitație generată!</p>
                    <p className="text-xs text-emerald-300">{createdInvite.email}</p>
                  </div>
                </div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">Link de trimis angajatului:</p>
                <div className="mb-4 break-all rounded-lg border border-zinc-800 bg-black/40 p-3 font-mono text-xs text-zinc-100">{createdInvite.link}</div>
                <button onClick={() => copyLink(createdInvite.link)}
                  className="mb-2 w-full rounded-lg bg-gradient-to-b from-orange-500 to-rose-500 py-2 text-sm font-medium text-white">
                  <Copy className="mr-2 inline h-4 w-4" /> {copied ? "Copiat!" : "Copiază link"}
                </button>
                <p className="text-[10px] text-zinc-500">💡 Trimite link-ul pe WhatsApp/email. Expiră în 7 zile.</p>
                <button onClick={() => { setShowInvite(false); setCreatedInvite(null); }} className="mt-3 w-full rounded-lg border border-zinc-800 bg-zinc-900 py-2 text-xs text-zinc-300">Închide</button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Edit access modal (existing users) */}
      {editAccessUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="edit-access-modal">
          <div className="w-full max-w-lg rounded-2xl border border-zinc-800 bg-zinc-950 shadow-2xl">
            <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-3">
              <div className="flex items-center gap-2">
                <KeyRound className="h-4 w-4 text-sky-400" />
                <div>
                  <h2 className="text-sm font-semibold text-zinc-100">Editează acces</h2>
                  <p className="text-[10px] text-zinc-500">{editAccessUser.name || editAccessUser.email}</p>
                </div>
              </div>
              <button data-testid="edit-access-close" onClick={() => setEditAccessUser(null)} className="rounded-md p-1.5 text-zinc-500 hover:bg-zinc-900 hover:text-zinc-300">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="max-h-[65vh] overflow-y-auto px-5 py-4 space-y-5">
              <div>
                <p className="mb-2 text-[10px] font-medium uppercase tracking-wider text-zinc-400">Pagini permise</p>
                <div className="grid grid-cols-2 gap-2">
                  {PAGES.map((p) => {
                    const on = accessForm.allowed_pages.includes(p.key);
                    return (
                      <button key={p.key} type="button" data-testid={`access-page-${p.key}`}
                        onClick={() => toggleAccessPage(p.key)}
                        className={`flex items-center justify-between rounded-lg border px-3 py-2 text-xs transition ${on ? "border-orange-500/50 bg-orange-500/10 text-orange-200" : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:text-zinc-200"}`}>
                        <span>{p.label}</span>
                        {on && <CheckCircle2 className="h-3.5 w-3.5" />}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <p className="mb-2 text-[10px] font-medium uppercase tracking-wider text-zinc-400">Acces board-uri Tasks</p>
                {boards.length === 0 ? (
                  <p className="text-xs text-zinc-600">Niciun board creat încă.</p>
                ) : (
                  <div className="space-y-2">
                    {boards.map((b) => {
                      const role = getAccessBoardRole(b.id);
                      return (
                        <div key={b.id} className="flex items-center justify-between gap-2 rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2">
                          <span className="truncate text-xs text-zinc-200">{b.name}</span>
                          <select data-testid={`access-board-role-${b.id}`} value={role}
                            onChange={(e) => setAccessBoardRole(b.id, e.target.value)}
                            className="rounded border border-zinc-800 bg-zinc-950 px-2 py-1 text-xs text-zinc-200">
                            <option value="">Fără acces</option>
                            <option value="viewer">Viewer</option>
                            <option value="editor">Editor</option>
                          </select>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-zinc-800 px-5 py-3">
              <button data-testid="edit-access-cancel" onClick={() => setEditAccessUser(null)}
                className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300 hover:text-zinc-100">
                Anulează
              </button>
              <button data-testid="edit-access-save" onClick={saveAccess} disabled={savingAccess}
                className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500 px-4 py-1.5 text-xs font-semibold text-zinc-950 hover:bg-emerald-400 disabled:opacity-50">
                {savingAccess ? "Se salvează..." : "Salvează acces"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
